import { BACKEND_URL } from '../config';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Leaf, ArrowLeft, MessageCircle, Plus, Send, User, Home } from 'lucide-react';

const Forum = ({ user, token, language }) => {
  const navigate = useNavigate();
  const { topicId } = useParams();
  
  const [topics, setTopics] = useState([]);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newComment, setNewComment] = useState('');
  
  // New topic form
  const [newTopic, setNewTopic] = useState({
    title: '',
    description: '',
    emoji: '🧵'
  });

  const t = {
    en: {
      forum: 'Farmer Forum',
      subtitle: 'Share experiences, ask questions, and learn together',
      createTopic: 'Create New Topic',
      allTopics: 'All Discussions',
      comments: 'Comments',
      addComment: 'Add your comment',
      submit: 'Submit',
      postedBy: 'Posted by',
      backToForum: 'Back to Forum',
      topicTitle: 'Topic Title',
      description: 'Description',
      chooseEmoji: 'Choose Emoji',
      cancel: 'Cancel',
      create: 'Create',
      noTopics: 'No topics yet. Be the first to start a discussion!',
      noComments: 'No comments yet. Be the first to comment!'
    },
    hi: {
      forum: 'किसान मंच',
      subtitle: 'अनुभव साझा करें, प्रश्न पूछें और एक साथ सीखें',
      createTopic: 'नया विषय बनाएं',
      allTopics: 'सभी चर्चाएं',
      comments: 'टिप्पणियाँ',
      addComment: 'अपनी टिप्पणी जोड़ें',
      submit: 'सबमिट करें',
      postedBy: 'द्वारा पोस्ट किया',
      backToForum: 'मंच पर वापस जाएं',
      topicTitle: 'विषय शीर्षक',
      description: 'विवरण',
      chooseEmoji: 'इमोजी चुनें',
      cancel: 'रद्द करें',
      create: 'बनाएं',
      noTopics: 'अभी तक कोई विषय नहीं। चर्चा शुरू करने वाले पहले व्यक्ति बनें!',
      noComments: 'अभी तक कोई टिप्पणी नहीं। टिप्पणी करने वाले पहले व्यक्ति बनें!'
    }
  };

  const text = language === 'hi' ? t.hi : t.en;

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/topics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTopics(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching topics:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchTopicDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/topics/${topicId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentTopic(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching topic:', err);
    } finally {
      setLoading(false);
    }
  }, [token, topicId]);

  useEffect(() => {
    if (topicId) {
      fetchTopicDetails();
    } else {
      fetchTopics();
    }
  }, [topicId, fetchTopics, fetchTopicDetails]);

  const handleCreateTopic = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/topics`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTopic)
      });
      
      if (response.ok) {
        setShowCreateModal(false);
        setNewTopic({ title: '', description: '', emoji: '🧵' });
        fetchTopics();
      }
    } catch (err) {
      console.error('Error creating topic:', err);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/forum/topics/${topicId}/comments`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ content: newComment })
        }
      );
      
      if (response.ok) {
        setNewComment('');
        fetchTopicDetails();
      }
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  const emojis = ['🧵', '🌾', '💡', '❓', '🚜', '🌱', '💰', '🐛', '☀️', '💧'];

  if (loading) {
    return (
      <div className="forum-page">
        <div className="loading-container">
          <MessageCircle size={48} className="spin" />
          <p>{language === 'hi' ? 'लोड हो रहा है...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  // Topic Details View
  if (topicId && currentTopic) {
    return (
      <div className="forum-page">
        <header className="forum-header">
          <div className="back-nav-group">
            <button className="back-btn" onClick={() => navigate('/forum')}>
              <ArrowLeft size={20} />
              {text.backToForum}
            </button>
            <button className="home-btn-alt" onClick={() => navigate('/')} title={language === 'hi' ? 'होम' : 'Home'}>
              <Home size={18} />
              {language === 'hi' ? 'होम' : 'Home'}
            </button>
          </div>
          <div className="header-content">
            <div className="logo" onClick={() => navigate('/')} style={{cursor: 'pointer'}}>
              <Leaf size={32} />
              <span>BoAI {text.forum}</span>
            </div>
          </div>
        </header>

        <div className="forum-content">
          <div className="topic-detail card">
            <div className="topic-header-detail">
              <span className="topic-emoji-large">{currentTopic.emoji}</span>
              <div className="topic-info-detail">
                <h2>{currentTopic.title}</h2>
                <div className="topic-meta">
                  <User size={16} />
                  <span>{text.postedBy}: {currentTopic.author_name}</span>
                  <span className="separator">•</span>
                  <span>{currentTopic.time_ago}</span>
                </div>
              </div>
            </div>
            <p className="topic-description">{currentTopic.description}</p>
          </div>

          <div className="comments-section card">
            <h3>
              <MessageCircle size={20} />
              {text.comments} ({currentTopic.comments?.length || 0})
            </h3>

            {currentTopic.comments && currentTopic.comments.length > 0 ? (
              <div className="comments-list">
                {currentTopic.comments.map((comment) => (
                  <div key={comment.id} className="comment-item">
                    <div className="comment-header">
                      <div className="comment-author">
                        <User size={16} />
                        <strong>{comment.author_name}</strong>
                      </div>
                      <span className="comment-time">{comment.time_ago}</span>
                    </div>
                    <p className="comment-content">{comment.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-content">{text.noComments}</p>
            )}

            <form className="add-comment-form" onSubmit={handleAddComment}>
              <h4>✏️ {text.addComment}</h4>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={language === 'hi' ? 'अपनी टिप्पणी लिखें...' : 'Write your comment...'}
                rows="4"
              />
              <button type="submit" className="btn-primary" disabled={!newComment.trim()}>
                <Send size={18} />
                {text.submit}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Topics List View
  return (
    <div className="forum-page">
      <header className="forum-header">
        <div className="back-nav-group">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={20} />
            {language === 'hi' ? 'डैशबोर्ड' : 'Dashboard'}
          </button>
          <button className="home-btn-alt" onClick={() => navigate('/')} title={language === 'hi' ? 'होम' : 'Home'}>
            <Home size={18} />
            {language === 'hi' ? 'होम' : 'Home'}
          </button>
        </div>
        <div className="header-content">
          <div className="logo" onClick={() => navigate('/')} style={{cursor: 'pointer'}}>
            <Leaf size={32} />
            <span>BoAI {text.forum}</span>
          </div>
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={20} />
            {text.createTopic}
          </button>
        </div>
      </header>

      <div className="forum-content">
        <div className="forum-intro">
          <h1>{text.forum}</h1>
          <p>{text.subtitle}</p>
        </div>

        <div className="topics-list">
          <h2>{text.allTopics}</h2>
          
          {topics.length > 0 ? (
            topics.map((topic) => (
              <div
                key={topic.id}
                className="topic-card card"
                onClick={() => navigate(`/forum/${topic.id}`)}
              >
                <div className="topic-header">
                  <span className="topic-emoji">{topic.emoji}</span>
                  <div className="topic-info">
                    <h3>{topic.title}</h3>
                    <div className="topic-meta">
                      <User size={14} />
                      <span>{topic.author_name}</span>
                      <span className="separator">•</span>
                      <span>{topic.time_ago}</span>
                    </div>
                  </div>
                  <div className="topic-stats">
                    <MessageCircle size={18} />
                    <span>{topic.comments_count}</span>
                  </div>
                </div>
                <p className="topic-description-preview">{topic.description}</p>
              </div>
            ))
          ) : (
            <p className="no-content">{text.noTopics}</p>
          )}
        </div>
      </div>

      {/* Create Topic Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{text.createTopic}</h2>
            <form onSubmit={handleCreateTopic}>
              <div className="form-group">
                <label>{text.chooseEmoji}</label>
                <div className="emoji-picker">
                  {emojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className={`emoji-btn ${newTopic.emoji === emoji ? 'selected' : ''}`}
                      onClick={() => setNewTopic({ ...newTopic, emoji })}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="form-group">
                <label>{text.topicTitle}</label>
                <input
                  type="text"
                  value={newTopic.title}
                  onChange={(e) => setNewTopic({ ...newTopic, title: e.target.value })}
                  placeholder={language === 'hi' ? 'विषय का शीर्षक...' : 'Topic title...'}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>{text.description}</label>
                <textarea
                  value={newTopic.description}
                  onChange={(e) => setNewTopic({ ...newTopic, description: e.target.value })}
                  placeholder={language === 'hi' ? 'विवरण...' : 'Description...'}
                  rows="4"
                  required
                />
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                  {text.cancel}
                </button>
                <button type="submit" className="btn-primary">
                  {text.create}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Forum;
